import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { APP_BUILDER_BUILD_TAB_PATH, CRM_CLIENTS_PAGE } from 'src/constants';
import { PortalConfigContext, useFeatureFlags } from 'src/context';
import { CRM_CLIENTS_TOUR_KEY } from 'src/features/crm/coachmark/stepHandlers';
import useNavigate from 'src/hooks/useNavigate';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { isAppBuilderDeployed } from 'src/routes/appSetup/builder/utils/isAppBuilderDeployed';
import {
  AppBuilder,
  useListAppBuildersQuery,
} from 'src/services/api/appBuilderApi';
import {
  useGetOnboardingTaskStatusQuery,
  useUpdateStudioTaskStatusMutation,
} from 'src/services/api/improvedOnboardingApi';
import {
  reopenCoachmark,
  setGettingStartedCompletedOnLoad,
  togglePrimarySidebarMobile,
} from 'src/store/ui/actions';
import { selectGettingStartedCompletedOnLoad } from 'src/store/ui/selectors';

import {
  areActivationStepsCompleted,
  getActivationStepCompletion,
  hasActivationTasks,
} from './activationSteps';
import {
  areStudioChecklistTasksCompleted,
  EXPLORE_PORTAL_TASK_ID,
  getStudioChecklistCompletionPct,
  getStudioChecklistTaskIds,
  getStudioChecklistTaskStatuses,
  GUIDED_TOUR_FIRST_CLIENT,
  GUIDED_TOUR_PARAM,
  STUDIO_CHECKLIST_TASK_CODE_BY_ID,
  StudioChecklistTask,
  StudioChecklistTaskId,
  StudioChecklistTaskStatus,
} from './studioChecklist';

// Fallback destinations. `publishApp` falls back to the new-build page only when
// the user has no draft to resume (see getMostRecentDraftAppBuilder below).
const TASK_ROUTES: Record<StudioChecklistTaskId, string> = {
  publishApp: APP_BUILDER_BUILD_TAB_PATH,
  // Carry the guided-tour intent so the CRM page starts the coachmark tour on arrival.
  explorePortal: `${CRM_CLIENTS_PAGE.path}?${GUIDED_TOUR_PARAM}=${GUIDED_TOUR_FIRST_CLIENT}`,
};

const APP_BUILDER_SESSION_PATH_PREFIX = '/app-setup/build/session/';

// A "draft" app has been created but never published and isn't in a terminal
// failed/stopped state — i.e. something the user can still take to publishing.
// Status counts as published too, because the list is fetched while a publish
// runs and can hold a row whose deploymentUrl hasn't landed yet.
const isDraftAppBuilder = (app: AppBuilder): boolean =>
  !isAppBuilderDeployed(app) &&
  app.status !== 'failed' &&
  app.status !== 'stopped';

// The most recently created draft, so "publish your first app" and "add another
// app" resume the app the user is building instead of starting them over. createdAt
// is an ISO-8601 string, so lexical comparison orders it chronologically.
const getMostRecentDraftAppBuilder = (
  apps: AppBuilder[] | undefined,
): AppBuilder | undefined => {
  const drafts = (apps ?? []).filter(isDraftAppBuilder);
  if (drafts.length === 0) return undefined;
  // Seedless reduce: the first draft seeds the accumulator, so `latest` is
  // always defined and the comparison keeps the newest createdAt.
  return drafts.reduce((latest, app) =>
    app.createdAt > latest.createdAt ? app : latest,
  );
};

export interface UseStudioChecklistResult {
  tasks: StudioChecklistTask[];
  /** Whole-number completion percentage (0-100) for the progress bar. */
  completionPct: number;
  isCollapsed: boolean;
  toggleCollapse: () => void;
  /** Navigates to the task's destination route. */
  onTaskClick: (taskId: StudioChecklistTaskId) => void;
  /** Navigates to a route and closes the primary sidebar on mobile. */
  navigateAndCloseMobileSidebar: (path: string) => void;
  /** The session of the newest unpublished app, when there is one to resume. */
  draftSessionPath: string | undefined;
  shouldRender: boolean;
  /**
   * True once every studio task is done. Drives the "You're all set" title while
   * the card stays mounted for the rest of the session (it hides on next load).
   */
  allTasksCompleted: boolean;
  /**
   * True when onboarding was already complete when the sidebar loaded — the state
   * that hides the checklist. The upgrade CTA takes the checklist's place, so it
   * keys off this snapshot rather than `allTasksCompleted`: completing the last
   * task mid-session keeps the checklist showing "You're all set" and defers the
   * CTA to the next load, so the two surfaces never appear at once.
   */
  completedOnLoad: boolean;
  /**
   * True when the workspace gets the onboarding-activation checklists (checklist
   * 1, the unlock card, checklist 2) instead of today's two-task card.
   */
  isActivation: boolean;
}

export const useStudioChecklist = (): UseStudioChecklistResult => {
  const { navigate } = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { EnableAppBuilder, EnableOnboardingActivation } = useFeatureFlags();
  // The workspace kind straight off portal config, not `useIsStudioUser` — the branch
  // below must key off Classic vs Studio alone, never off the FTUX flag's state.
  const { isStudioUser } = useContext(PortalConfigContext);
  const { data: onboardingStatus, isSuccess } = useGetOnboardingTaskStatusQuery(
    {},
  );
  const [updateStudioTaskStatus] = useUpdateStudioTaskStatusMutation();
  const { data: builderApps } = useListAppBuildersQuery(undefined, {
    skip: !EnableAppBuilder,
  });
  const isPrimarySidebarOpenOnMobile = useAppSelector(
    (state) => state.ui.primarySideBarMobile.isOpen,
  );

  // Collapse is ephemeral UI state — not worth persisting.
  const [isCollapsed, setIsCollapsed] = useState(false);
  const toggleCollapse = useCallback(() => setIsCollapsed((prev) => !prev), []);

  const navigateAndCloseMobileSidebar = useCallback(
    (path: string) => {
      navigate(path);
      // Match sidebar nav items: close the primary sidebar on mobile after navigating.
      if (isPrimarySidebarOpenOnMobile) {
        dispatch(togglePrimarySidebarMobile({ isOpen: false }));
      }
    },
    [navigate, dispatch, isPrimarySidebarOpenOnMobile],
  );

  const draftSessionPath = useMemo(() => {
    const draft = getMostRecentDraftAppBuilder(builderApps);
    return draft ? `${APP_BUILDER_SESSION_PATH_PREFIX}${draft.id}` : undefined;
  }, [builderApps]);

  const taskStatuses = useMemo(
    () =>
      getStudioChecklistTaskStatuses(onboardingStatus?.additionalFields.tasks),
    [onboardingStatus],
  );

  // Classic workspaces only: they get no test client and no CRM coachmark tour, so the
  // guided "sign in as your client" flow that completes this task server-side can never
  // run. Nothing else moves the task to done for them, so the click records it here.
  // This reads the task status, not a client count — a classic workspace with clients
  // can still read as not-done (the task list is withheld entirely once creditsAwarded
  // or dismissed is set), so it must not be treated as proof the CRM is empty.
  const isExploreDone =
    taskStatuses[EXPLORE_PORTAL_TASK_ID] === StudioChecklistTaskStatus.Done;
  const completeExploreWithoutClient = !isStudioUser && !isExploreDone;

  const onTaskClick = useCallback(
    (taskId: StudioChecklistTaskId) => {
      let route = TASK_ROUTES[taskId];
      if (taskId === EXPLORE_PORTAL_TASK_ID) {
        if (completeExploreWithoutClient) {
          void updateStudioTaskStatus({
            taskCode: STUDIO_CHECKLIST_TASK_CODE_BY_ID[EXPLORE_PORTAL_TASK_ID],
            status: StudioChecklistTaskStatus.Done,
          });
        }
        // ClientsPage renders the coachmark tour for Studio workspaces only, and its
        // first step waits on a client row an empty CRM never renders, so classic
        // carries no guided-tour intent.
        if (!isStudioUser) {
          route = CRM_CLIENTS_PAGE.path;
        } else if (!isExploreDone) {
          // Clicking "Sign in as client" ends the tour for the session, but the task
          // only completes once the preview signs in, so a return trip resumes it.
          dispatch(reopenCoachmark(CRM_CLIENTS_TOUR_KEY));
        }
      }
      if (taskId === 'publishApp' && draftSessionPath) {
        route = draftSessionPath;
      }
      navigateAndCloseMobileSidebar(route);
    },
    [
      navigateAndCloseMobileSidebar,
      draftSessionPath,
      completeExploreWithoutClient,
      isStudioUser,
      isExploreDone,
      updateStudioTaskStatus,
      dispatch,
    ],
  );

  const hasTasks = (onboardingStatus?.additionalFields.tasks ?? []).length > 0;
  // The rows, the progress bar and the completion state all read off this one
  // set, so the card can never say "You're all set" beside an unfinished row.
  const taskIds = useMemo(
    () => getStudioChecklistTaskIds(onboardingStatus?.additionalFields.tasks),
    [onboardingStatus],
  );

  const tasks: StudioChecklistTask[] = useMemo(() => {
    const taskLabels: Record<StudioChecklistTaskId, string> = {
      publishApp: t(
        ($) => $.page_apps.app_builder.getting_started.tasks.publish_app,
      ),
      explorePortal: t(
        ($) => $.page_apps.app_builder.getting_started.tasks.explore_portal,
      ),
    };
    return taskIds.map((id) => ({
      id,
      label: taskLabels[id],
      status: taskStatuses[id],
    }));
  }, [t, taskIds, taskStatuses]);

  const completionPct = getStudioChecklistCompletionPct(taskStatuses, taskIds);

  const isActivation =
    EnableOnboardingActivation &&
    hasActivationTasks(onboardingStatus?.additionalFields.tasks);

  const allTasksCompleted = isActivation
    ? areActivationStepsCompleted(
        getActivationStepCompletion(onboardingStatus?.additionalFields.tasks),
      )
    : areStudioChecklistTasksCompleted(taskIds, taskStatuses);

  // Snapshot the "already completed" state only once real task data has arrived.
  // A success response that momentarily lacks tasks would otherwise lock it to a
  // stale `false`, so a later refetch delivering completed tasks could never hide
  // the checklist for a done user. It's shared across SidebarNav instances so the
  // mobile drawer, which mounts on open, doesn't take its own snapshot after the
  // last step and skip "You're all set".
  const storedCompletedOnLoad = useAppSelector(
    selectGettingStartedCompletedOnLoad,
  );
  const completedOnLoad =
    storedCompletedOnLoad ?? (isSuccess && hasTasks ? allTasksCompleted : null);
  useEffect(() => {
    if (storedCompletedOnLoad === null && completedOnLoad !== null) {
      dispatch(setGettingStartedCompletedOnLoad(completedOnLoad));
    }
  }, [storedCompletedOnLoad, completedOnLoad, dispatch]);
  const hiddenOnLoad = completedOnLoad === true;

  return {
    tasks,
    completionPct,
    isCollapsed,
    toggleCollapse,
    onTaskClick,
    navigateAndCloseMobileSidebar,
    draftSessionPath,
    shouldRender: isSuccess && !hiddenOnLoad,
    allTasksCompleted,
    completedOnLoad: hiddenOnLoad,
    isActivation,
  };
};
